// acura-frontend/src/components/navBarMenus/NavbarMenu.js

import { useState } from "react";
import { usePermissions, useTranslate, useResourceDefinitions } from "react-admin";
import MultiLevelMenuItem from "../MultiLevelMenuItem";
import { 
  Button, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Box
} from "@mui/material";
import { Icon } from "@iconify-icon/react";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HomeIcon from "@mui/icons-material/Home"; 

const NavbarMenu = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const resources = useResourceDefinitions();
  const isAdministrator = permissions?.administrator;

  // State untuk setiap dropdown menu
  const [anchorTransaction, setAnchorTransaction] = useState(null);
  const [anchorReport, setAnchorReport] = useState(null);
  const [anchorMaster, setAnchorMaster] = useState(null);
  const [anchorSync, setAnchorSync] = useState(null);

  // Handlers
  const handleOpenTransaction = (event) => setAnchorTransaction(event.currentTarget);
  const handleCloseTransaction = () => setAnchorTransaction(null);
  
  const handleOpenReport = (event) => setAnchorReport(event.currentTarget);
  const handleCloseReport = () => setAnchorReport(null);
  
  const handleOpenMaster = (event) => setAnchorMaster(event.currentTarget);
  const handleCloseMaster = () => setAnchorMaster(null);

  const handleOpenSync = (event) => setAnchorSync(event.currentTarget);
  const handleCloseSync = () => setAnchorSync(null);
  
  const handleGoToDashboard = () => {
    navigate("/");
  };

  // Handler baru yang support Ctrl+Click
  const handleNavigate = (path, closeMenu) => (event) => {
    // Jika Ctrl (Windows/Linux) atau Cmd (Mac) ditekan, biarkan browser handle secara default
    if (event.ctrlKey || event.metaKey) {
      closeMenu();
      return; // Biarkan link href bekerja
    }
    
    // Jika tidak, prevent default dan gunakan navigate
    event.preventDefault();
    navigate(path);
    closeMenu();
  };

  const menuButtonStyle = { 
    color: "inherit", 
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
      border: 'none',
    },
    '&:hover': {
      border: 'none',
    },
    border: 'none',
  };

  if (!permissions) return null;

  return (
    <Box sx={{ display: "flex", gap: 1, ml: 2 }}>      
      {/* DASHBOARD BUTTON */}
      <Button
          onClick={handleGoToDashboard}
          startIcon={<HomeIcon />} 
          sx={menuButtonStyle}
      >
          Dashboard
      </Button>

      {/* SYNC MENU */}
      <Button
          onClick={handleOpenSync}
          endIcon={<KeyboardArrowDownIcon />}
          sx={menuButtonStyle}
      >
          Sync
      </Button>
      <Menu
          anchorEl={anchorSync}
          open={Boolean(anchorSync)}
          onClose={handleCloseSync}
          PaperProps={{
            style: {
              maxHeight: 500,
            },
          }}
      >
        {isAdministrator && (
          <MenuItem
            component="a"
            href="/#/sync-monitor"
            onClick={handleNavigate("/sync-monitor", handleCloseSync)}
          >
            <ListItemText>Monitoring Sync Depo</ListItemText>
          </MenuItem>
        )}
        {isAdministrator && (
          <MenuItem component="a" href="/#/sync-queue" onClick={handleNavigate("/sync-queue", handleCloseSync)}>
            <ListItemText>Sync Queue</ListItemText>
          </MenuItem>
        )}
        {isAdministrator && (
          <MenuItem component="a" href="/#/sync-history" onClick={handleNavigate("/sync-history", handleCloseSync)}>
            <ListItemText>Sync History</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          component="a"
          href="/#/sync-pull"
          onClick={handleNavigate("/sync-pull", handleCloseSync)}
        >
          <ListItemText>Sync Pull</ListItemText>
        </MenuItem>
        {isAdministrator && (
          <MenuItem
            component="a"
            href="/#/sync-propagations"
            onClick={handleNavigate("/sync-propagations", handleCloseSync)}
          >
            <ListItemText>Sync Propagation</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* MASTER DATA MENU */}
      <Button
          onClick={handleOpenMaster}
          endIcon={<KeyboardArrowDownIcon />}
          sx={menuButtonStyle}
      >
          Master Data
      </Button>
      <Menu
          anchorEl={anchorMaster}
          open={Boolean(anchorMaster)}
          onClose={handleCloseMaster}
          PaperProps={{
            style: {
              maxHeight: 500,
            },
          }}
      >

        {(isAdministrator || permissions.list_coa) && (
          <MenuItem
            component="a"
            href="/#/coa"
            onClick={handleNavigate("/coa", handleCloseMaster)}
          >              
            <ListItemText>{t("resources.chart_of_account.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_salesman) && (
          <MenuItem 
            component="a" 
            href="/#/salesmen"
            onClick={handleNavigate("/salesmen", handleCloseMaster)}
          >            
            <ListItemText>Salesmen</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_salesman_target) && (
          <MenuItem 
            component="a" 
            href="/#/salesman-targets"
            onClick={handleNavigate("/salesman-targets", handleCloseMaster)}
          >
            <ListItemText>Target Box Salesman</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_salesman_incentive_rate_rule) && (
          <MenuItem 
            component="a" 
            href="/#/salesman-incentive-rate-rules"
            onClick={handleNavigate("/salesman-incentive-rate-rules", handleCloseMaster)}
          >
            <ListItemText>Salesman Incentive Rate</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_sales_area) && (
          <MenuItem 
            component="a" 
            href="/#/sales-areas"
            onClick={handleNavigate("/sales-areas", handleCloseMaster)}
          >            
            <ListItemText>{t("resources.sales_area.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_transport) && (
          <MenuItem 
            component="a" 
            href="/#/transports"
            onClick={handleNavigate("/transports", handleCloseMaster)}
          >            
            <ListItemText>{t("resources.transport.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_supplier) && (
          <MenuItem 
            component="a" 
            href="/#/suppliers"
            onClick={handleNavigate("/suppliers", handleCloseMaster)}
          >            
            <ListItemText>{t("resources.supplier.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_customer) && (
          <MenuItem 
            component="a" 
            href="/#/customers"
            onClick={handleNavigate("/customers", handleCloseMaster)}
          >           
            <ListItemText>{t("resources.customer.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_product) && (
          <MenuItem 
            component="a" 
            href="/#/products"
            onClick={handleNavigate("/products", handleCloseMaster)}
          >              
            <ListItemText>{t("resources.product.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_warehouse) && (
          <MenuItem 
            component="a" 
            href="/#/warehouses"
            onClick={handleNavigate("/warehouses", handleCloseMaster)}
          >              
            <ListItemText>{t("resources.warehouse.name")}</ListItemText>
          </MenuItem>
        )}

        <Divider/>

        {(isAdministrator || permissions.list_company) && (
          <MenuItem 
            component="a" 
            href="/#/companies"
            onClick={handleNavigate("/companies", handleCloseMaster)}
          >            
            <ListItemText>Master Depo</ListItemText>
          </MenuItem>
        )}

        {isAdministrator && (
          <>
            <MenuItem 
              component="a" 
              href="/#/users"
              onClick={handleNavigate("/users", handleCloseMaster)}
            >              
              <ListItemText>Master Pengguna</ListItemText>
            </MenuItem>
            <MenuItem 
              component="a" 
              href="/#/roles"
              onClick={handleNavigate("/roles", handleCloseMaster)}
            >              
              <ListItemText>Master Roles</ListItemText>
            </MenuItem>
            <MenuItem 
              component="a" 
              href="/#/permissions"
              onClick={handleNavigate("/permissions", handleCloseMaster)}
            >              
              <ListItemText>Master Permissions</ListItemText>
            </MenuItem>
          </>
        )}

        {(isAdministrator || permissions.list_customer_type) && (
          <MenuItem 
            component="a" 
            href="/#/customer-types"
            onClick={handleNavigate("/customer-types", handleCloseMaster)}
          >            
            <ListItemText>Master {t("resources.customer_type.name")}</ListItemText>
          </MenuItem>
        )}       

        {(isAdministrator || permissions.list_category) && (
          <MenuItem 
            component="a" 
            href="/#/categories"
            onClick={handleNavigate("/categories", handleCloseMaster)}
          >              
            <ListItemText>Master {t("resources.category.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_kind) && (
          <MenuItem 
            component="a" 
            href="/#/kinds"
            onClick={handleNavigate("/kinds", handleCloseMaster)}
          >            
            <ListItemText>Master {t("resources.kind.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_brand || permissions.list_size || permissions.list_motif || permissions.list_density || permissions.list_color || permissions.list_quality) && (
            <MultiLevelMenuItem label='Master Kode Produk'>
                {(isAdministrator || permissions.list_brand) && (
                    <MenuItem 
                      component="a" 
                      href="/#/brands"
                      onClick={handleNavigate("/brands", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.brand.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_size) && (
                    <MenuItem 
                      component="a" 
                      href="/#/sizes"
                      onClick={handleNavigate("/sizes", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.size.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_motif) && (
                    <MenuItem 
                      component="a" 
                      href="/#/motifs"
                      onClick={handleNavigate("/motifs", handleCloseReport)}
                    >
                        <ListItemText>Motif</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_density) && (
                    <MenuItem 
                      component="a" 
                      href="/#/densities"
                      onClick={handleNavigate("/densities", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.density.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_color) && (
                    <MenuItem 
                      component="a" 
                      href="/#/colors"
                      onClick={handleNavigate("/colors", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.color.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_quality) && (
                    <MenuItem 
                      component="a" 
                      href="/#/qualities"
                      onClick={handleNavigate("/qualities", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.quality.name")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {(isAdministrator || permissions.list_price) && (
          <MenuItem 
            component="a" 
            href="/#/prices"
            onClick={handleNavigate("/prices", handleCloseMaster)}
          >            
            <ListItemText>Master {t("resources.price.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_price_limit) && (
          <MenuItem 
            component="a" 
            href="/#/price-limits"
            onClick={handleNavigate("/price-limits", handleCloseMaster)}
          >           
            <ListItemText>Master {t("resources.price_limit.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_brand_price_addition) && (
          <MenuItem 
            component="a" 
            href="/#/brand-price-additions"
            onClick={handleNavigate("/brand-price-additions", handleCloseMaster)}
          >            
            <ListItemText>{t("resources.brand_price_addition.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_size_price_addition) && (
          <MenuItem 
            component="a" 
            href="/#/size-price-additions"
            onClick={handleNavigate("/size-price-additions", handleCloseMaster)}
          >            
            <ListItemText>{t("resources.size_price_addition.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_promotion) && (
          <MenuItem 
            component="a" 
            href="/#/promotions"
            onClick={handleNavigate("/promotions", handleCloseMaster)}
          >              
            <ListItemText>{t("mine.label.promotions")}</ListItemText>
          </MenuItem>
        )}        

        {(isAdministrator || permissions.list_setting) && (
          <MenuItem 
            component="a" 
            href="/#/settings"
            onClick={handleNavigate("/settings", handleCloseMaster)}
          >              
            <ListItemText>{t("resources.settings.name")}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* TRANSACTION MENU */}
      <Button
        onClick={handleOpenTransaction}
        endIcon={<KeyboardArrowDownIcon />}
        sx={menuButtonStyle}
      >
        {t("mine.label.transaction")}
      </Button>
      <Menu
          anchorEl={anchorTransaction}
          open={Boolean(anchorTransaction)}
          onClose={handleCloseTransaction}
          PaperProps={{
            style: {
              maxHeight: 500,
            },
          }}
      >
          {/* Bad Debt */}
          {(isAdministrator || permissions.list_bad_debt || permissions.list_bad_debt_installment || permissions.list_bad_debt_correction || permissions.list_bad_debt_check_cashing) && (
              <MultiLevelMenuItem label={t("mine.expand_menu.bad_debt")}>
                  {(isAdministrator || permissions.list_bad_debt) && (
                      <MenuItem 
                        component="a" 
                        href="/#/bad-debts"
                        onClick={handleNavigate("/bad-debts", handleCloseTransaction)}
                      >
                          <ListItemText>{t("resources.bad_debt.name")}</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_bad_debt_installment) && (
                      <MenuItem 
                        component="a" 
                        href="/#/bad-debt-installments"
                        onClick={handleNavigate("/bad-debt-installments", handleCloseTransaction)}
                      >
                          <ListItemText>{t("resources.bad_debt_installment.name")}</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_bad_debt_correction) && (
                      <MenuItem 
                        component="a" 
                        href="/#/bad-debt-corrections"
                        onClick={handleNavigate("/bad-debt-corrections", handleCloseTransaction)}
                      >
                          <ListItemText>{t("resources.bad_debt_correction.name")}</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_bad_debt_check_cashing) && (
                      <MenuItem 
                        component="a" 
                        href="/#/bad-debt-check-cashing"
                        onClick={handleNavigate("/bad-debt-check-cashing", handleCloseTransaction)}
                      >
                          <ListItemText>{t("resources.bad_debt_check_cashing.name")}</ListItemText>
                      </MenuItem>
                  )}
              </MultiLevelMenuItem>
          )}
          
          {/* Restock Plan */}
          {(isAdministrator || permissions.list_restock_plan) && (
            <MenuItem 
              component="a" 
              href="/#/restock-plans"
              onClick={handleNavigate("/restock-plans", handleCloseTransaction)}
            >            
                <ListItemText>Restock Plan</ListItemText>
            </MenuItem>
          )}
          
          {/* PURCHASE MENU */}
          {(isAdministrator || permissions.list_purchase_order || permissions.list_purchase || permissions.list_purchase_return) && (
              <MultiLevelMenuItem label={t("mine.expand_menu.purchase")}>
                  {(isAdministrator || permissions.list_purchase_order) && (
                      <MenuItem 
                        component="a" 
                        href="/#/purchase-orders"
                        onClick={handleNavigate("/purchase-orders", handleCloseReport)}
                      >
                          <ListItemText>Surat Pesanan</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_purchase) && (
                      <MenuItem 
                        component="a" 
                        href="/#/purchases"
                        onClick={handleNavigate("/purchases", handleCloseReport)}
                      >
                          <ListItemText>Pembelian</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_purchase_return) && (
                      <MenuItem 
                        component="a" 
                        href="/#/purchase-returns"
                        onClick={handleNavigate("/purchase-returns", handleCloseReport)}
                      >
                          <ListItemText>Retur Pembelian</ListItemText>
                      </MenuItem>
                  )}
              </MultiLevelMenuItem>
          )}
          
          {/* SALES MENU */}
          {(isAdministrator || permissions.list_sale_order || permissions.list_sale || permissions.list_sale_return) && (
              <MultiLevelMenuItem label={t("mine.expand_menu.sale")}>
                  {(isAdministrator || permissions.list_sale_order) && (
                      <MenuItem 
                        component="a" 
                        href="/#/sale-orders"
                        onClick={handleNavigate("/sale-orders", handleCloseReport)}
                      >
                          <ListItemText>Surat Pesanan</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_sale) && (
                      <MenuItem 
                        component="a" 
                        href="/#/sales"
                        onClick={handleNavigate("/sales", handleCloseReport)}
                      >
                          <ListItemText>Penjualan</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_sale_return) && (
                      <MenuItem 
                        component="a" 
                        href="/#/sale-returns"
                        onClick={handleNavigate("/sale-returns", handleCloseReport)}
                      >
                          <ListItemText>Retur Penjualan</ListItemText>
                      </MenuItem>
                  )}
              </MultiLevelMenuItem>
          )}
          
          {/* Mutasi */}
          {(isAdministrator || permissions.list_mutation_repacking || permissions.list_mutation_inventory_write_off) && (
              <MultiLevelMenuItem label={t("mine.expand_menu.mutation")}>
                  {(isAdministrator || permissions.list_mutation_repacking) && (
                      <MenuItem 
                        component="a" 
                        href="/#/mutation-repackings"
                        onClick={handleNavigate("/mutation-repackings", handleCloseTransaction)}
                      >
                          <ListItemText>{t("resources.mutation_repacking.name")}</ListItemText>
                      </MenuItem>
                  )}
                  {(isAdministrator || permissions.list_mutation_inventory_write_off) && (
                      <MenuItem 
                        component="a" 
                        href="/#/mutation-inventory-write-offs"
                        onClick={handleNavigate("/mutation-inventory-write-offs", handleCloseTransaction)}
                      >
                          <ListItemText>{t("resources.mutation_inventory_write_off.name")}</ListItemText>
                      </MenuItem>
                  )}                                      
              </MultiLevelMenuItem>
          )}
          
          {/* ACCOUNT PAYABLE (HUTANG) */}          
          {(isAdministrator || permissions.list_factory_debt_payment || permissions.list_factory_debt_correction) && (
              <MultiLevelMenuItem label={t("mine.expand_menu.factory_debt")}>
                  {(isAdministrator || permissions.list_factory_debt_correction) && (
                      <MenuItem 
                        component="a" 
                        href="/#/factory-debt-corrections"
                        onClick={handleNavigate("/factory-debt-corrections", handleCloseReport)}
                      >
                          <ListItemText>{t("resources.factory_debt_correction.name")}</ListItemText>
                      </MenuItem>
                  )}                    
                  {(isAdministrator || permissions.list_factory_debt_payment) && (
                      <MenuItem 
                        component="a" 
                        href="/#/factory-debt-payments"
                        onClick={handleNavigate("/factory-debt-payments", handleCloseReport)}
                      >
                          <ListItemText>{t("resources.factory_debt_payment.name")}</ListItemText>
                      </MenuItem>
                  )}
              </MultiLevelMenuItem>
          )}

          {/* ACCOUNT RECEIVABLE (PIUTANG) */}
          {(isAdministrator || permissions.list_customer_debt_payment || permissions.list_customer_debt_correction) && (
              <MultiLevelMenuItem label={t("mine.expand_menu.customer_debt")}>
                  {(isAdministrator || permissions.list_customer_debt_correction) && (
                      <MenuItem 
                        component="a" 
                        href="/#/customer-debt-corrections"
                        onClick={handleNavigate("/customer-debt-corrections", handleCloseReport)}
                      >
                          <ListItemText>{t("resources.customer_debt_correction.name")}</ListItemText>
                      </MenuItem>
                  )}                                      
                  {(isAdministrator || permissions.list_customer_debt_payment) && (
                      <MenuItem 
                        component="a" 
                        href="/#/customer-debt-payments"
                        onClick={handleNavigate("/customer-debt-payments", handleCloseReport)}
                      >
                          <ListItemText>{t("resources.customer_debt_payment.name")}</ListItemText>
                      </MenuItem>
                  )}
              </MultiLevelMenuItem>
          )}

         {(isAdministrator || permissions.list_check_cashing) && (
          <MenuItem 
            component="a" 
            href="/#/check-cashings"
            onClick={handleNavigate("/check-cashings", handleCloseTransaction)}
          >            
              <ListItemText>Pencairan Cek/Giro</ListItemText>
          </MenuItem>
          )}
          

          {/* Jurnal */}
          {(isAdministrator || permissions.list_journal) && (
          <>
          {(isAdministrator || permissions.list_journal) && (
          <MenuItem 
            component="a" 
            href="/#/journals"
            onClick={handleNavigate("/journals", handleCloseTransaction)}
          >                
              <ListItemText>{t("resources.journal.name") || "Jurnal"}</ListItemText>
          </MenuItem>
          )}

          {/* Inventory Pending */}
          {(isAdministrator || permissions.list_inventory_pending) && (
          <MenuItem 
            component="a" 
            href="/#/inventory-pendings"
            onClick={handleNavigate("/inventory-pendings", handleCloseTransaction)}
          >                
              <ListItemText>{t("resources.inventory_pending.name")}</ListItemText>
          </MenuItem>
          )}
          </>
          )}
      </Menu>

      <Button
          component="a"
          href="/#/close-books"
          onClick={handleNavigate("/close-books", handleCloseTransaction)}
          sx={menuButtonStyle}
      >
          Tutup Periode
      </Button>

      {/* REPORT MENU */}
      <Button
        onClick={handleOpenReport}
        endIcon={<KeyboardArrowDownIcon />}
        sx={menuButtonStyle}
      >
        {t("mine.label.report")}
      </Button>
      <Menu
        anchorEl={anchorReport}
        open={Boolean(anchorReport)}
        onClose={handleCloseReport}
        PaperProps={{
          style: {
            maxHeight: 500,
          },
        }}
      >
        {(isAdministrator || permissions.list_balance_sheet) && (
          <MenuItem 
            component="a" 
            href="/#/balance-sheets"
            onClick={handleNavigate("/balance-sheets", handleCloseReport)}
          >            
            <ListItemText>{t("resources.balance_sheets.name")}</ListItemText>
          </MenuItem>
        )}
        
        {(isAdministrator || permissions.list_income_statement) && (
          <MenuItem 
            component="a" 
            href="/#/income-statements"
            onClick={handleNavigate("/income-statements", handleCloseReport)}
          >            
            <ListItemText>{t("resources.income_statements.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_cost_of_goods_sold) && (
          <MenuItem 
            component="a" 
            href="/#/cost-of-goods-solds"
            onClick={handleNavigate("/cost-of-goods-solds", handleCloseReport)}
          >            
            <ListItemText>{t("resources.cost_of_goods_solds.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_general_ledger || permissions.list_all_transaction_journal || permissions.list_journal_memorial) && (
            <MultiLevelMenuItem label={t("mine.expand_menu.general_ledger")}>
                {(isAdministrator || permissions.list_general_ledger) && (
                    <MenuItem 
                      component="a" 
                      href="/#/general-ledgers"
                      onClick={handleNavigate("/general-ledgers", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.general_ledgers.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_all_transaction_journal) && (
                    <MenuItem 
                      component="a" 
                      href="/#/all-transaction-journals"
                      onClick={handleNavigate("/all-transaction-journals", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.all_transaction_journals.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_journal_memorial) && (
                    <MenuItem 
                      component="a" 
                      href="/#/journal-memorials"
                      onClick={handleNavigate("/journal-memorials", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.journal_memorials.name")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* PURCHASE */}
        {(isAdministrator || permissions.list_purchase_net_card || permissions.list_purchase_card || permissions.list_purchase_return_card || permissions.list_purchase_goods_in_transit) && (
            <MultiLevelMenuItem label={t("mine.expand_menu.purchase")}>
                {(isAdministrator || permissions.list_purchase_net_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/purchase-net-cards"
                      onClick={handleNavigate("/purchase-net-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("mine.label.net")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_purchase_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/purchase-cards"
                      onClick={handleNavigate("/purchase-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("mine.label.gross")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_purchase_return_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/purchase-return-cards"
                      onClick={handleNavigate("/purchase-return-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.return.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_purchase_goods_in_transit) && (
                    <MenuItem 
                      component="a" 
                      href="/#/purchase-goods-in-transits"
                      onClick={handleNavigate("/purchase-goods-in-transits", handleCloseReport)}
                    >
                        <ListItemText>Goods in Transit</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* SALE */}
        {(isAdministrator || permissions.list_sale_net_card || permissions.list_sale_card || permissions.list_sale_return_card || permissions.list_sale_ranking) && (
            <MultiLevelMenuItem label={t("mine.expand_menu.sale")}>
                {(isAdministrator || permissions.list_sale_net_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/sale-net-cards"
                      onClick={handleNavigate("/sale-net-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("mine.label.net")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_sale_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/sale-cards"
                      onClick={handleNavigate("/sale-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("mine.label.gross")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_sale_return_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/sale-return-cards"
                      onClick={handleNavigate("/sale-return-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.return.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_sale_ranking) && (
                    <MenuItem 
                      component="a" 
                      href="/#/sale-rankings"
                      onClick={handleNavigate("/sale-rankings", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.ranking.name")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* ACCOUNT RECEIVABLES */}
        {(isAdministrator || permissions.list_account_receivable_payment || permissions.list_account_receivable_correction || permissions.list_account_receivable_pcm || permissions.list_account_receivable_card || permissions.list_account_receivable_pcm_card || permissions.list_account_receivable_aging || permissions.list_account_receivable_pcm_age || permissions.list_account_receivable_pcm_overview || permissions.list_account_receivable_history || permissions.list_account_receivable_bad_debt_history) && (
            <MultiLevelMenuItem label={t("mine.expand_menu.account_receivables")}>
                {(isAdministrator || permissions.list_account_receivable_payment) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-payments"
                      onClick={handleNavigate("/account-receivable-payments", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_payments.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_correction) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-corrections"
                      onClick={handleNavigate("/account-receivable-corrections", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_corrections.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_pcm) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-pcms"
                      onClick={handleNavigate("/account-receivable-pcms", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_pcms.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-cards"
                      onClick={handleNavigate("/account-receivable-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_cards.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_pcm_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-pcm-cards"
                      onClick={handleNavigate("/account-receivable-pcm-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_pcm_cards.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_aging) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-agings"
                      onClick={handleNavigate("/account-receivable-agings", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_agings.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_pcm_age) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-pcm-ages"
                      onClick={handleNavigate("/account-receivable-pcm-ages", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_pcm_ages.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_history) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-histories"
                      onClick={handleNavigate("/account-receivable-histories", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_histories.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_pcm_overview) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-pcm-overviews"
                      onClick={handleNavigate("/account-receivable-pcm-overviews", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_pcm_overviews.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_receivable_bad_debt_history) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-receivable-bad-debt-histories"
                      onClick={handleNavigate("/account-receivable-bad-debt-histories", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_receivable_bad_debt_histories.name")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* ACCOUNT PAYABLES */}
        {(isAdministrator || permissions.list_account_payable_card || permissions.list_account_payable_age || permissions.list_account_payable_history || permissions.list_account_payable_payment || permissions.list_account_payable_correction) && (
            <MultiLevelMenuItem label={t("mine.expand_menu.account_payables")}>
                {(isAdministrator || permissions.list_account_payable_payment) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-payable-payments"
                      onClick={handleNavigate("/account-payable-payments", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_payable_payments.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_payable_correction) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-payable-corrections"
                      onClick={handleNavigate("/account-payable-corrections", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_payable_corrections.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_payable_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-payable-cards"
                      onClick={handleNavigate("/account-payable-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_payable_cards.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_payable_age) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-payable-ages"
                      onClick={handleNavigate("/account-payable-ages", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_payable_ages.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_account_payable_history) && (
                    <MenuItem 
                      component="a" 
                      href="/#/account-payable-histories"
                      onClick={handleNavigate("/account-payable-histories", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.account_payable_histories.name")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* INVENTORIES */}
        {(isAdministrator || permissions.list_inventory_card || permissions.list_inventory_age || permissions.list_inventory_history || permissions.list_inventory_repacking || permissions.list_inventory_write_off) && (
            <MultiLevelMenuItem label={t("mine.expand_menu.inventories")}>
                {(isAdministrator || permissions.list_inventory_card) && (
                    <MenuItem 
                      component="a" 
                      href="/#/inventory-cards"
                      onClick={handleNavigate("/inventory-cards", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.inventory_cards.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_inventory_age) && (
                    <MenuItem 
                      component="a" 
                      href="/#/inventory-ages"
                      onClick={handleNavigate("/inventory-ages", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.inventory_ages.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_inventory_history) && (
                    <MenuItem 
                      component="a" 
                      href="/#/inventory-histories"
                      onClick={handleNavigate("/inventory-histories", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.inventory_histories.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_inventory_repacking) && (
                    <MenuItem 
                      component="a" 
                      href="/#/inventory-repackings"
                      onClick={handleNavigate("/inventory-repackings", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.inventory_repackings.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_inventory_write_off) && (
                    <MenuItem 
                      component="a" 
                      href="/#/inventory-write-offs"
                      onClick={handleNavigate("/inventory-write-offs", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.inventory_write_offs.name")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* PAYMENT CHECK */}
        {(isAdministrator || permissions.list_received_check || permissions.list_cashed_returned_check) && (
            <MultiLevelMenuItem label={t("mine.expand_menu.payment_check")}>
                {(isAdministrator || permissions.list_received_check) && (
                    <MenuItem 
                      component="a" 
                      href="/#/received-checks"
                      onClick={handleNavigate("/received-checks", handleCloseReport)}
                    >
                        <ListItemText>{t("mine.label.received")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_cashed_returned_check) && (
                    <MenuItem 
                      component="a" 
                      href="/#/cashed-returned-checks"
                      onClick={handleNavigate("/cashed-returned-checks", handleCloseReport)}
                    >
                        <ListItemText>{t("mine.label.cashed_returned")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* CUSTOMER */}
        {(isAdministrator || permissions.list_customer_list || permissions.list_customer_ranking || permissions.list_customer_membership) && (
            <MultiLevelMenuItem label={t("mine.label.customer")}>
                {(isAdministrator || permissions.list_customer_list) && (
                    <MenuItem 
                      component="a" 
                      href="/#/customer-lists"
                      onClick={handleNavigate("/customer-lists", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.customer_lists.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_customer_ranking) && (
                    <MenuItem 
                      component="a" 
                      href="/#/customer-rankings"
                      onClick={handleNavigate("/customer-rankings", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.customer_rankings.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_customer_membership) && (
                    <MenuItem 
                      component="a" 
                      href="/#/customer-memberships"
                      onClick={handleNavigate("/customer-memberships", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.customer_memberships.name")}</ListItemText>
                    </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* SALESMAN */}
        {(isAdministrator || permissions.list_salesman_incentive || permissions.list_salesman_journal || permissions.list_salesman_penalty_detail) && (
            <MultiLevelMenuItem label={t("mine.label.salesman")}>
                {(isAdministrator || permissions.list_salesman_incentive) && (
                    <MenuItem 
                      component="a" 
                      href="/#/salesman-incentives"
                      onClick={handleNavigate("/salesman-incentives", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.salesman_incentives.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_salesman_journal) && (
                    <MenuItem 
                      component="a" 
                      href="/#/salesman-journals"
                      onClick={handleNavigate("/salesman-journals", handleCloseReport)}
                    >
                        <ListItemText>{t("resources.salesman_journals.name")}</ListItemText>
                    </MenuItem>
                )}
                {(isAdministrator || permissions.list_salesman_penalty_detail) && (
                  <MenuItem 
                    component="a" 
                    href="/#/salesman-penalty-details"
                    onClick={handleNavigate("/salesman-penalty-details", handleCloseReport)}
                  >
                    <ListItemText>{t("resources.salesman_penalty_details.name")}</ListItemText>
                  </MenuItem>
                )}
            </MultiLevelMenuItem>
        )}

        {/* MENU ITEM SATUAN */}
        {(isAdministrator || permissions.list_product_ranking) && (
            <MenuItem 
              component="a" 
              href="/#/product-rankings"
              onClick={handleNavigate("/product-rankings", handleCloseReport)}
            >                
                <ListItemText>{t("resources.product_rankings.name")}</ListItemText>
            </MenuItem>
        )}

        {(isAdministrator || permissions.list_transaction_history) && (
          <MenuItem
            component="a"
            href="/#/transaction-histories"
            onClick={handleNavigate("/transaction-histories", handleCloseReport)}
          >
            <ListItemText>{t("resources.transaction_histories.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_transaction_history) && (
          <MenuItem
            component="a"
            href="/#/print-histories"
            onClick={handleNavigate("/print-histories", handleCloseReport)}
          >
            <ListItemText>{t("resources.print_histories.name")}</ListItemText>
          </MenuItem>
        )}

        {(isAdministrator || permissions.list_activity) && (
            <MenuItem 
              component="a" 
              href="/#/activities"
              onClick={handleNavigate("/activities", handleCloseReport)}
            >                    
              <ListItemText>{t("resources.activity.name")}</ListItemText>
            </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default NavbarMenu;
